import { useMemo } from 'react'
import useCharterFlightJourneysForHotelDates from 'hooks/CharterFlights/useCharterFlightJourneysForHotelDates'
import { getSeatsLeftFromJourney } from './utils'
import { FlightsClassTypes } from 'constants/flight'

/**
 * Custom hook to fetch flight data for a single calendar item using charter flight journeys
 *
 * This hook uses the same data source as useCharterFlightPrice to ensure consistency:
 * - Uses useAllCharterFlightJourneys to get all flights
 * - Matches journeys by checkIn/checkOut dates
 * - Uses fare families for accurate pricing
 * - Extracts seatsLeft from journey data
 *
 * @param params - Flight search parameters including checkIn/checkOut dates
 * @returns Flight data including fetching state, prices, and journey
 */
export function useFlightItemData(params: {
  departDate: string
  returnDate: string
  checkIn: string
  checkOut: string
  rooms: Array<App.Occupants>
  fareClass: FlightsClassTypes
  charterBundleRoute?: App.CharterBundleRoute
}) {
  const { checkIn, checkOut, rooms, fareClass, charterBundleRoute } = params

  // Use useCharterFlightJourneysForHotelDates to get flight data (same as useCharterFlightPrice)
  const {
    departingJourney,
    returningJourney,
    fetching,
    departingFareFamily,
    returningFareFamily,
  } = useCharterFlightJourneysForHotelDates({
    charterBundleRoute,
    checkInDate: checkIn,
    checkOutDate: checkOut,
    occupancies: rooms,
    fareClass,
    disabled: !charterBundleRoute,
  })

  // Calculate flight prices and seatsLeft
  const flightPricing = useMemo(() => {
    // Calculate total flight fare from fare families (same as useCharterFlightPrice)
    let totalFlightFare = 0
    if (departingFareFamily && returningFareFamily) {
      totalFlightFare = departingFareFamily.cost.all + returningFareFamily.cost.all
    } else if (departingJourney && returningJourney) {
      // Fallback to journey prices if fare families not available yet
      const departingFare = departingJourney.price?.all?.totalFare ?? 0
      const returningFare = returningJourney.price?.all?.totalFare ?? 0
      totalFlightFare = departingFare + returningFare
    }

    // Get minimum seatsLeft from both departing and returning flights
    const departingSeatsLeft = getSeatsLeftFromJourney(departingJourney)
    const returningSeatsLeft = getSeatsLeftFromJourney(returningJourney)
    const seatsLeft = departingSeatsLeft !== undefined && returningSeatsLeft !== undefined
      ? Math.min(departingSeatsLeft, returningSeatsLeft)
      : departingSeatsLeft ?? returningSeatsLeft

    return {
      fetching,
      totalFlightFare,
      departingFlight: departingJourney,
      seatsLeft,
      returningFlight: returningJourney,
    }
  }, [departingJourney, returningJourney, departingFareFamily, returningFareFamily, fetching])

  return flightPricing
}
